/**
 * « Opportunites » (WEA-1572 / WEA-1573) n'est pas une nouvelle table : c'est une
 * LECTURE des notifications, filtree sur les types qui annoncent un bien.
 *
 * La maquette y range quatre filtres — Tout, Nouveaux biens, Suggestions Omny,
 * Suggestions agences. Le moteur de suggestion fabrique `suggestion_continuation`
 * et `suggestion_recommandation_hebdo` (Suggestions Omny) ; les suggestions
 * agences n'ont aucun producteur et le filtre reste masque (WEA-1573, arbitrage
 * Jeremy du 2026-09-10).
 *
 * Le fil est PAR PROJET (WEA-1757) : une suggestion est fabriquee pour un projet,
 * et l'afficher dans un autre montre des biens a Bordeaux dans une recherche a
 * Vincennes.
 */

import type { Notification, NotificationType } from '$lib/types/notification';
import type { t } from 'svelte-i18n';

/** Le formateur `$t` — svelte-i18n n'exporte pas son type. */
type MessageFormatter = typeof t extends { subscribe: (run: (value: infer V) => void) => unknown }
	? V
	: never;

export type OpportunityFilter = 'all' | 'new_listings' | 'omny_suggestions' | 'agency_suggestions';

/** Types de notification qui alimentent chaque filtre. */
const TYPES_BY_FILTER: Record<Exclude<OpportunityFilter, 'all'>, NotificationType[]> = {
	// `price_drop` rejoint « Nouveaux biens » : du point de vue de l'utilisateur
	// c'est la meme promesse — un bien qui vient d'entrer dans sa cible.
	// `new_listing` et `price_drop` restent en reserve, sans producteur actif
	// aujourd'hui. `suggestion_nouveautes` est le type des alertes « nouveau
	// bien » (WEA-1738), qui l'ont repris apres son extinction (WEA-1736) : les
	// notifications emises AVANT restent masquees (WEA-1757, decision du
	// 2026-09-17), voir `NOUVEAUTES_VISIBLES_DEPUIS`.
	new_listings: ['new_listing', 'price_drop', 'suggestion_nouveautes'],
	// `suggestion_recommandation_hebdo` (WEA-1737) rejoue la meme recherche que la
	// Continuation, une semaine plus tard puis chaque semaine : meme promesse,
	// donc meme filtre. Sans cette entree la notification sortirait de l'onglet.
	// `suggestion_neuf` (WEA-1808) : programmes neufs proposes par Omny, hors
	// du cadre de la recherche -- une suggestion, pas une alerte. Sans cette
	// entree la notification serait invisible dans le fil (WEA-1523, WEA-1803),
	// et le lien du courriel neuf ouvrirait un fil sans sa tuile.
	omny_suggestions: [
		'suggestion',
		'suggestion_continuation',
		'suggestion_recommandation_hebdo',
		'suggestion_neuf'
	],
	agency_suggestions: ['agency_suggestion']
};

/** Tous les types qui apparaissent dans l'onglet, quel que soit le filtre. */
export const OPPORTUNITY_TYPES: NotificationType[] = Object.values(TYPES_BY_FILTER).flat();

/**
 * `suggestion_nouveautes` a servi deux fois. Les notifications de l'ancien
 * producteur, eteint par WEA-1736, restent masquees (WEA-1757) ; les alertes
 * « nouveau bien » de WEA-1738 reprennent le type. En production, les
 * dernieres anciennes datent du 2026-09-15 et les premieres alertes du
 * 2026-09-23 : la date les separe sans ambiguite (WEA-1803).
 */
export const NOUVEAUTES_VISIBLES_DEPUIS = new Date('2026-09-16T00:00:00Z');

export function isOpportunity(notification: Notification): boolean {
	if (!OPPORTUNITY_TYPES.includes(notification.type)) return false;
	return (
		notification.type !== 'suggestion_nouveautes' ||
		notification.createdAt >= NOUVEAUTES_VISIBLES_DEPUIS
	);
}

/**
 * Les notifications d'un projet (`metadata.project_id`). Sans projet — l'accueil —
 * tout passe. Dans un projet, une notification sans projet est ecartee : on ne
 * sait pas a quelle recherche elle repond.
 */
export function forProject(
	notifications: Notification[],
	projectId: string | null | undefined
): Notification[] {
	if (!projectId) return notifications;
	return notifications.filter((n) => n.projectId === projectId);
}

/**
 * Filtre une liste de notifications pour l'onglet Opportunites.
 * L'ordre d'entree est conserve — c'est deja l'ordre chronologique de la requete.
 */
export function filterOpportunities(
	notifications: Notification[],
	filter: OpportunityFilter = 'all'
): Notification[] {
	if (filter === 'all') return notifications.filter(isOpportunity);
	const types = TYPES_BY_FILTER[filter];
	return notifications.filter((n) => isOpportunity(n) && types.includes(n.type));
}

/**
 * Nombre d'opportunites non lues — c'est ce que porte la pastille de navigation.
 * Meme perimetre que le fil : le projet ouvert, ou tout sans projet.
 */
export function countUnreadOpportunities(
	notifications: Notification[],
	projectId?: string | null
): number {
	return forProject(notifications, projectId).filter((n) => isOpportunity(n) && !n.isRead).length;
}

/** Les deux logiques du fil : l'alerte (nouveaux biens) et la suggestion (Omny sort du cadre). */
export type OpportunityKind = 'alert' | 'suggestion';

export function opportunityKind(type: NotificationType): OpportunityKind {
	return isNewListingsType(type) ? 'alert' : 'suggestion';
}

/** Le type releve-t-il de l'alerte « nouveaux biens » (corail) plutot que de la suggestion Omny (bleu) ? */
export function isNewListingsType(type: NotificationType): boolean {
	return TYPES_BY_FILTER.new_listings.includes(type);
}

/**
 * Titre d'une carte de suggestion : « 4 nouveaux biens du 15 sept. » ou
 * « 3 suggestions du 12 sept. ». Le nombre vient de `listingCount`
 * (`metadata.listing_count`) ; sans lui — notifications anterieures a la
 * colonne — on garde le titre ecrit par le moteur.
 */
export function opportunityTitle(
	notification: Notification,
	t: MessageFormatter,
	locale = 'fr-FR'
): string {
	if (notification.listingCount === undefined) return notification.title;
	const date = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(
		notification.createdAt
	);
	const key =
		opportunityKind(notification.type) === 'alert'
			? 'opportunities.titleAlert'
			: notification.type === 'suggestion_neuf'
				? 'opportunities.titleNewBuilds'
				: 'opportunities.titleSuggestion';
	return t(key, { values: { count: notification.listingCount, date } });
}

/** Parametre d'URL qui ouvre une tuile du fil Opportunites (WEA-1759). */
export const OPPORTUNITY_QUERY_PARAM = 'opportunity';

/**
 * Ou s'ouvre la tuile : la page plein ecran `/opportunites` (mobile, ou le fil
 * est une case de la MobileNav) ou le chat du projet, dont le panneau de droite
 * porte l'onglet Opportunites (desktop et tablette, WEA-1766).
 */
export type OpportunitySurface = 'page' | 'chat';

/**
 * Destination d'une notification d'opportunite : le fil du projet qui l'a
 * produite, ouvert sur sa tuile. La notification est un LIEN, pas un
 * conteneur — les biens ne se deplient plus sous elle (WEA-1759).
 *
 * Sur desktop, le fil vit dans le panneau de droite du chat : y envoyer la
 * page plein ecran donnait un ecran unique dans l'application, sans la
 * conversation (retour Jeremy, WEA-1766). `surface: 'chat'` ouvre donc le
 * projet lui-meme avec le meme parametre.
 *
 * `fallbackProjectId` couvre les notifications anterieures a
 * `metadata.project_id` (WEA-1757) : on les ouvre dans le projet courant.
 * Rend `null` hors perimetre Opportunites, ou sans aucun projet.
 */
export function opportunityHref(
	notification: Notification,
	fallbackProjectId?: string | null,
	surface: OpportunitySurface = 'page'
): string | null {
	if (!isOpportunity(notification)) return null;
	const projectId = notification.projectId ?? fallbackProjectId;
	if (!projectId) return null;
	const path = surface === 'chat' ? `/project/${projectId}` : `/project/${projectId}/opportunites`;
	return `${path}?${OPPORTUNITY_QUERY_PARAM}=${notification.id}`;
}
